import { Info } from '@mui/icons-material';
import { Box, Card, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/molecules/dialog';
import { PageHeader } from 'src/components/molecules/page-header';
import { useDialog } from 'src/hooks/use-dialog';

import { useGDPRClause } from '../../hooks/query/use-gdpr-clause';
import RemoveGdprClauseDialog from '../organisms/gdpr/dialogs/remove-gdpr-clause-dialog';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { GdprClauseType, GdprLanguage } from '@/features/settings/api/types/gdpr';
import { UpdateGdprClauseDialog } from '@/features/settings/components/organisms/gdpr/dialogs/update-gdpr-clause-dialog/update-gdpr-clause-dialog';
import { GdprClausesTable } from '@/features/settings/components/organisms/gdpr/gdpr-clause-table';
import { withPermissions } from '@/permissions/can';

export interface PickedClause {
  type: GdprClauseType;
  language: GdprLanguage;
}

const SettingsGdpr = () => {
  const { t } = useTranslation(['settings', 'common']);

  const { data, error, isLoading } = useGDPRClause();

  const [showAllClauseDialog, handleOpenShowAllClauseDialog, handleCloseShowAllClauseDialog] =
    useDialog();

  const [isOpenUpdateClauseDialog, handleOpenUpdateClauseDialog, handleCloseUpdateClauseDialog] =
    useDialog();

  const [
    isOpenRemoveGdprClauseDialog,
    handleOpenRemoveGdprClauseDialog,
    handleCloseRemoveGdprClauseDialog,
  ] = useDialog();

  const [pickedClause, setPickedClause] = useState<PickedClause | null>(null);

  const onCloseUpdateGdprClauseDialog = () => {
    handleCloseUpdateClauseDialog();
    setPickedClause(null);
  };

  const selectedClause = pickedClause
    ? data?.[pickedClause?.type]?.find(clause => clause.language === pickedClause?.language)
    : null;

  return (
    <>
      <PageHeader title={t('settings:gdpr.title')} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: 2,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
          {!isLoading ? (
            data &&
            Object.keys(data)?.map(gdprType => (
              <Box key={gdprType}>
                <Box
                  sx={{
                    mb: 1,
                  }}>
                  <Typography variant="h4" sx={{ mb: 0 }}>
                    {t(`settings:gdpr.${gdprType as GdprClauseType}Title`)}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Info />
                    {t(`settings:gdpr.${gdprType as GdprClauseType}TitleDescription`)}
                  </Typography>
                </Box>
                <Card variant="outlined">
                  <GdprClausesTable
                    data={Object.values(data[gdprType as GdprClauseType])}
                    handleOpenRemoveGdprClauseDialog={handleOpenRemoveGdprClauseDialog}
                    handleOpenUpdateClauseDialog={handleOpenUpdateClauseDialog}
                    handleOpenShowAllClauseDialog={handleOpenShowAllClauseDialog}
                    setPickedClause={setPickedClause}
                  />
                </Card>
              </Box>
            ))
          ) : (
            <DataStatusIndicator
              dataLength={data ? 1 : 0}
              isFetching={isLoading}
              error={error || ''}
            />
          )}
        </Box>
      </Box>
      <RemoveGdprClauseDialog
        gdprClauseId={selectedClause?.id || 0}
        open={isOpenRemoveGdprClauseDialog}
        onCancel={handleCloseRemoveGdprClauseDialog}
      />
      <Dialog
        title={t(`settings:gdpr.${pickedClause?.language || GdprLanguage.ENGLISH}Clause`)}
        open={showAllClauseDialog}
        variant="warning"
        onCancel={handleCloseShowAllClauseDialog}
        isConfirmButtonDisabled={true}
        cancelButtonText={t('common:button.close')}>
        <Typography>{selectedClause?.gdprClause}</Typography>
      </Dialog>
      <UpdateGdprClauseDialog
        open={isOpenUpdateClauseDialog}
        onCancel={onCloseUpdateGdprClauseDialog}
        selectedClauseLanguage={selectedClause?.language || GdprLanguage.ENGLISH}
        selectedGdprClause={selectedClause?.gdprClause || ''}
        selectedGdprClauseId={selectedClause?.id || 0}
        selectedGdprClauseType={pickedClause?.type || GdprClauseType.GUEST}
      />
    </>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'GDPR'), SettingsGdpr);
